import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import DOMPurify from "dompurify";
import { Loader2, ArrowLeft, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { supabase } from "@/integrations/supabase/client";
import SEO from "@/components/SEO";

interface BlogPostData {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  content: string;
  cover_image_url: string | null;
  published_at: string | null;
  created_at: string;
}

/** Simple Markdown-to-HTML renderer for blog content */
const renderMarkdown = (md: string): string => {
  let html = md
    // headings
    .replace(/^### (.+)$/gm, "<h3 class='font-heading text-xl font-semibold text-foreground mt-8 mb-3'>$1</h3>")
    .replace(/^## (.+)$/gm, "<h2 class='font-heading text-2xl font-bold text-foreground mt-10 mb-4'>$1</h2>")
    .replace(/^# (.+)$/gm, "<h1 class='font-heading text-3xl font-bold text-foreground mt-10 mb-4'>$1</h1>")
    // images
    .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, "<img src='$2' alt='$1' class='rounded-xl w-full my-6 shadow-soft' loading='lazy' />")
    // bold & italic
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    // links
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, "<a href='$2' class='text-primary hover:underline'>$1</a>")
    // horizontal rule
    .replace(/^---$/gm, "<hr class='border-border my-8' />")
    // unordered list items
    .replace(/^[-*] (.+)$/gm, "<li class='ml-4 list-disc text-foreground'>$1</li>")
    // ordered list items
    .replace(/^\d+\. (.+)$/gm, "<li class='ml-4 list-decimal text-foreground'>$1</li>")
    // paragraphs (double newline)
    .replace(/\n\n/g, "</p><p class='text-foreground leading-relaxed mb-4'>");

  return DOMPurify.sanitize(`<p class='text-foreground leading-relaxed mb-4'>${html}</p>`);
};

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPostData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPost = async () => {
      if (!slug) return;
      const { data } = await supabase
        .from("blog_posts")
        .select("*")
        .eq("slug", slug)
        .eq("status", "published")
        .maybeSingle();
      if (data) setPost(data as BlogPostData);
      setLoading(false);
    };
    fetchPost();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-24 pb-16 container mx-auto px-4 text-center">
          <p className="text-muted-foreground mb-4">Post not found.</p>
          <Button variant="outline" asChild>
            <Link to="/blog">Back to Blog</Link>
          </Button>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title={post.title}
        description={post.excerpt || undefined}
        image={post.cover_image_url || undefined}
        type="article"
        article={{ publishedTime: post.published_at || post.created_at }}
      />
      <Header />
      <main className="pt-24 pb-16">
        <article className="container mx-auto px-4 max-w-3xl">
          <Button variant="ghost" size="sm" className="mb-6" asChild>
            <Link to="/blog">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Blog
            </Link>
          </Button>

          {post.cover_image_url && (
            <div className="aspect-[16/9] rounded-2xl overflow-hidden mb-8 shadow-soft">
              <img
                src={post.cover_image_url}
                alt={post.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
            <Calendar className="w-4 h-4" />
            {new Date(post.published_at || post.created_at).toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </div>

          <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-8">
            {post.title}
          </h1>

          <div
            className="prose-custom"
            dangerouslySetInnerHTML={{
              __html: renderMarkdown(
                post.cover_image_url
                  ? post.content.replace(
                      new RegExp(`!\\[[^\\]]*\\]\\(${post.cover_image_url.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\)\\n*`),
                      ""
                    )
                  : post.content
              ),
            }}
          />

          <div className="mt-12 pt-8 border-t border-border text-center">
            <p className="text-muted-foreground mb-4">
              Ready to create a custom song for your special moment?
            </p>
            <Button variant="hero" asChild>
              <Link to="/create">Get Started</Link>
            </Button>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default BlogPost;
